import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/**
 * Delay before MegaMenuHoverEvent fires.
 */
const HOVER_COMMIT_DELAY_MS = 150;

/**
 * When one top-level menu is already open,
 * wait this long before switching to another top-level menu.
 */
const MENU_SWITCH_DELAY_MS = 300;

/**
 * Grace period before closing the open menu
 * after the pointer actually leaves the navigation.
 */
const MENU_CLOSE_DELAY_MS = 250;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem
 * @property {HTMLElement | null} activeOverflowItem
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} overflowMenu
 * @property {HTMLElement[]} [submenu]
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  /**
   * @type {ReturnType<typeof setTimeout> | undefined}
   */
  #hoverDispatchTimer;

  /**
   * @type {ReturnType<typeof setTimeout> | undefined}
   */
  #menuSwitchTimer;

  /**
   * @type {HTMLElement | null}
   */
  #menuSwitchItem = null;

  /**
   * @type {ReturnType<typeof setTimeout> | undefined}
   */
  #menuCloseTimer;

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(this.#preloadImages);

    window.addEventListener(
      'resize',
      this.#resizeListener
    );

    this.addEventListener(
      'keydown',
      this.#onKeydown
    );

    this.overflowMenu?.addEventListener(
      'pointerleave',
      this.#overflowSubmenuListener
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    window.removeEventListener(
      'resize',
      this.#resizeListener
    );

    this.removeEventListener(
      'keydown',
      this.#onKeydown
    );

    document.body.removeEventListener(
      'pointermove',
      this.#onPointerMove
    );

    if (this.#state.activeItem) {
      this.#stopPointerTracking(
        this.#state.activeItem
      );
    }

    this.overflowMenu?.removeEventListener(
      'pointerleave',
      this.#overflowSubmenuListener
    );

    this.#cleanupMutationObserver();

    clearTimeout(
      this.#hoverDispatchTimer
    );

    this.#hoverDispatchTimer =
      undefined;

    this.#cancelMenuSwitch();

    clearTimeout(
      this.#menuCloseTimer
    );

    this.#menuCloseTimer =
      undefined;
  }

  /**
   * Recalculate menu style after resizing.
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
  }, 100);

  /**
   * Overflow menu exit.
   */
  #overflowSubmenuListener = () => {
    this.#scheduleMenuClose();
  };

  /**
   * Close submenu with Escape.
   *
   * @param {KeyboardEvent} event
   */
  #onKeydown = (event) => {
    if (event.key !== 'Escape') {
      return;
    }

    const item =
      this.#state.activeItem;

    if (!item) {
      return;
    }

    const control =
      this.#focusTargetAfterClose(
        item
      );

    this.#deactivate(
      item,
      { force: true }
    );

    control.focus();
  };

  /**
   * Toggle submenu from disclosure button.
   *
   * @param {PointerEvent | MouseEvent} event
   */
  toggle = (event) => {
    const button =
      event.target;

    if (
      !(button instanceof HTMLElement)
    ) {
      return;
    }

    const item =
      findMenuItem(button);

    if (!item) {
      return;
    }

    const isExpanded =
      button.getAttribute(
        'aria-expanded'
      ) === 'true';

    if (isExpanded) {
      this.#deactivate(
        this.#state.activeItem ?? item,
        { force: true }
      );

      const focusTarget =
        this.#focusTargetAfterClose(
          item
        );

      if (focusTarget !== button) {
        focusTarget.focus();
      }
    } else {
      this.activate(event);
    }
  };

  /**
   * Determine where focus should return
   * after a submenu closes.
   *
   * @param {HTMLElement} item
   * @returns {HTMLElement}
   */
  #focusTargetAfterClose(item) {
    const listItem =
      item.closest(
        '.menu-list__list-item'
      );

    const slot =
      listItem instanceof HTMLElement
        ? listItem.slot
        : '';

    if (
      slot === 'overflow' ||
      slot === 'more'
    ) {
      const moreButton =
        this.querySelector(
          '[slot="more"] [ref="menuitem"]'
        );

      if (
        moreButton instanceof HTMLElement
      ) {
        return moreButton;
      }
    }

    return this.#expandableFor(
      item
    );
  }

  /**
   * Return the element carrying aria-expanded.
   *
   * @param {HTMLElement} item
   * @returns {HTMLElement}
   */
  #expandableFor(item) {
    const listItem =
      item.closest(
        '.menu-list__list-item'
      );

    const disclosure =
      listItem?.querySelector(
        '[ref="disclosure[]"]'
      );

    return (
      disclosure instanceof HTMLElement
        ? disclosure
        : item
    );
  }

  /**
   * Current menu state.
   *
   * @type {State}
   */
  #state = {
    activeItem: null,
    activeOverflowItem: null,
  };

  /**
   * Pointer idle timer.
   *
   * @type {ReturnType<typeof setTimeout> | undefined}
   */
  #pointerIdleTimer;

  /**
   * Last known pointer position.
   *
   * @type {Object}
   */
  #lastPointer = {
    x: 0,
    y: 0,
  };

  /**
   * Track pointer movement for Atelier's
   * existing safety-box behavior.
   *
   * @param {PointerEvent} event
   */
  #onPointerMove = (event) => {
    const activeLink =
      this.#state.activeItem;

    if (!activeLink) {
      return;
    }

    this.#lastPointer.x =
      event.clientX;

    this.#lastPointer.y =
      event.clientY;

    const moving =
      Math.abs(event.movementX) >= 1 ||
      Math.abs(event.movementY) >= 1;

    activeLink.dataset.safetyBox =
      `${moving}`;

    clearTimeout(
      this.#pointerIdleTimer
    );

    if (moving) {
      this.#pointerIdleTimer =
        setTimeout(() => {
          if (
            this.#state.activeItem
          ) {
            this.#state.activeItem
              .dataset
              .safetyBox = 'false';

            this.#reconcilePointerTarget();
          }
        }, 50);
    } else {
      this.#reconcilePointerTarget();
    }
  };

  /**
   * Re-check the element currently
   * underneath the pointer.
   */
  #reconcilePointerTarget() {
    const {
      x,
      y,
    } = this.#lastPointer;

    requestAnimationFrame(() => {
      const target =
        document.elementFromPoint(
          x,
          y
        );

      if (!target) {
        return;
      }

      const listItem =
        target.closest(
          '.menu-list__list-item'
        );

      if (
        listItem &&
        !listItem.contains(
          this.#state.activeItem
        )
      ) {
        listItem.dispatchEvent(
          new PointerEvent(
            'pointerenter',
            {
              bubbles: false,
            }
          )
        );
      }
    });
  }

  /**
   * Begin pointer safety-box tracking.
   *
   * @param {HTMLElement} item
   * @param {HTMLElement | null} previousItem
   */
  #startPointerTracking(
    item,
    previousItem
  ) {
    if (previousItem) {
      this.#stopPointerTracking(
        previousItem
      );
    } else {
      document.body.addEventListener(
        'pointermove',
        this.#onPointerMove
      );
    }

    const rect =
      item.getBoundingClientRect();

    const isOverlap =
      this.headerComponent
        ?.hasAttribute(
          'data-submenu-overlap-bottom-row'
        );

    const boundary =
      isOverlap
        ? this.headerComponent
            ?.querySelector(
              '.header__row--top'
            )
        : this.headerComponent;

    const bottom =
      boundary
        ?.getBoundingClientRect()
        .bottom ?? 0;

    item.style.setProperty(
      '--box-height',
      `${bottom - rect.top}px`
    );
  }

  /**
   * Stop pointer safety-box tracking.
   *
   * @param {HTMLElement} item
   */
  #stopPointerTracking(item) {
    window.clearTimeout(
      this.#pointerIdleTimer
    );

    this.#pointerIdleTimer =
      undefined;

    item.style.removeProperty(
      '--box-height'
    );

    delete item.dataset.safetyBox;
  }

  /**
   * Overflow menu element.
   */
  get overflowMenu() {
    return (
      this.refs.overflowMenu
        ?.shadowRoot
        ?.querySelector(
          '[part="overflow"]'
        ) ?? null
    );
  }

  /**
   * Whether overflow list is hovered.
   *
   * @returns {boolean}
   */
  get overflowListHovered() {
    return (
      this.refs.overflowMenu
        ?.shadowRoot
        ?.querySelector(
          '[part="overflow-list"]'
        )
        ?.matches(':hover') ??
      false
    );
  }

  /**
   * First overflow item.
   *
   * @returns {HTMLElement | null}
   */
  #getFirstOverflowMenuItem() {
    const menuItem =
      this.refs.overflowMenu
        ?.querySelector(
          '[slot="overflow"] [ref="menuitem"]'
        );

    return (
      menuItem instanceof HTMLElement
        ? menuItem
        : null
    );
  }

  /**
   * Header component.
   */
  get headerComponent() {
    return this.closest(
      'header-component'
    );
  }

  /**
   * Entry point for menu activation.
   *
   * First menu opens immediately.
   * Switching from an already-open menu
   * requires a short hover-intent delay.
   *
   * @param {PointerEvent | FocusEvent | MouseEvent} event
   */
  activate = (event) => {
    if (
      !(event.target instanceof Element)
    ) {
      return;
    }

    if (!this.headerComponent) {
      return;
    }

    const item =
      findMenuItem(
        event.target
      );

    if (!item) {
      return;
    }

    /**
     * Entering any valid menu target
     * means a pending close should stop.
     */
    clearTimeout(
      this.#menuCloseTimer
    );

    this.#menuCloseTimer =
      undefined;

    /**
     * Pointer returned to the menu
     * that is already open.
     */
    if (
      item ===
      this.#state.activeItem
    ) {
      this.#cancelMenuSwitch();

      return;
    }

    /**
     * If another top-level menu is
     * already open, don't instantly
     * switch just because the pointer
     * briefly crossed this item.
     */
    if (
      event.type === 'pointerenter' &&
      this.#state.activeItem
    ) {
      this.#scheduleMenuSwitch(
        item
      );

      return;
    }

    /**
     * First activation, keyboard focus,
     * and click/toggle remain immediate.
     */
    this.#cancelMenuSwitch();

    this.#activateItem(
      item,
      event instanceof FocusEvent
    );
  };

  /**
   * Schedule switching to another
   * top-level menu.
   *
   * @param {HTMLElement} item
   */
  #scheduleMenuSwitch(item) {
    this.#cancelMenuSwitch();

    const listItem =
      item.closest(
        '.menu-list__list-item'
      );

    if (
      !(listItem instanceof HTMLElement)
    ) {
      return;
    }

    this.#menuSwitchItem =
      item;

    this.#menuSwitchTimer =
      setTimeout(() => {
        this.#menuSwitchTimer =
          undefined;

        if (
          this.#menuSwitchItem !== item
        ) {
          return;
        }

        this.#menuSwitchItem =
          null;

        /**
         * User merely crossed over the item.
         * Don't switch.
         */
        if (
          !listItem.matches(':hover')
        ) {
          return;
        }

        this.#activateItem(
          item,
          false
        );
      }, MENU_SWITCH_DELAY_MS);
  }

  /**
   * Cancel a pending top-level menu switch.
   */
  #cancelMenuSwitch() {
    clearTimeout(
      this.#menuSwitchTimer
    );

    this.#menuSwitchTimer =
      undefined;

    this.#menuSwitchItem =
      null;
  }

  /**
   * Actually activate a menu item.
   *
   * This preserves Atelier's original
   * activation behavior after the
   * hover-intent decision is made.
   *
   * @param {HTMLElement} item
   * @param {boolean} isFocusEvent
   */
  #activateItem(
    item,
    isFocusEvent = false
  ) {
    if (!this.headerComponent) {
      return;
    }

    if (
      item ===
      this.#state.activeItem
    ) {
      return;
    }

    clearTimeout(
      this.#menuCloseTimer
    );

    this.#menuCloseTimer =
      undefined;

    this.#cancelMenuSwitch();

    const listItem =
      item.closest(
        '.menu-list__list-item'
      );

    const slot =
      listItem instanceof HTMLElement
        ? listItem.slot
        : '';

    const isMoreTrigger =
      slot === 'more';

    const isDefaultSlot =
      slot === '';

    const overflowItem =
      isMoreTrigger
        ? this.#getFirstOverflowMenuItem()
        : null;

    this.dataset.overflowExpanded =
      (!isDefaultSlot).toString();

    const previouslyActiveItem =
      this.#state.activeItem;

    const previouslyActiveOverflowItem =
      this.#state.activeOverflowItem;

    if (previouslyActiveItem) {
      this.#expandableFor(
        previouslyActiveItem
      ).ariaExpanded = 'false';

      const previousSubmenu =
        findSubmenu(
          previouslyActiveItem
        );

      if (previousSubmenu) {
        previousSubmenu.inert =
          true;
      }
    }

    if (
      previouslyActiveOverflowItem &&
      previouslyActiveOverflowItem !==
        previouslyActiveItem
    ) {
      this.#expandableFor(
        previouslyActiveOverflowItem
      ).ariaExpanded = 'false';

      const previousOverflowSubmenu =
        findSubmenu(
          previouslyActiveOverflowItem
        );

      if (
        previousOverflowSubmenu
      ) {
        previousOverflowSubmenu.inert =
          true;
      }
    }

    this.#state.activeItem =
      item;

    this.#state.activeOverflowItem =
      overflowItem;

    this.ariaExpanded =
      'true';

    this.#expandableFor(
      item
    ).ariaExpanded = 'true';

    if (
      overflowItem &&
      overflowItem !== item
    ) {
      this.#expandableFor(
        overflowItem
      ).ariaExpanded = 'true';

      const activeOverflowSubmenu =
        findSubmenu(
          overflowItem
        );

      if (
        activeOverflowSubmenu
      ) {
        activeOverflowSubmenu.inert =
          false;
      }
    }

    const overflowItemSubmenu =
      isMoreTrigger
        ? findSubmenu(
            overflowItem
          )
        : null;

    let submenu =
      findSubmenu(item) ||
      (
        overflowItemSubmenu
          ? this.overflowMenu
          : null
      );

    const hasSubmenu =
      Boolean(submenu);

    if (
      !hasSubmenu &&
      !isDefaultSlot
    ) {
      submenu =
        this.overflowMenu;
    }

    if (submenu) {
      clearTimeout(
        this.#hoverDispatchTimer
      );

      this.#hoverDispatchTimer =
        undefined;

      const committedItem =
        item;

      if (isFocusEvent) {
        this.dispatchEvent(
          new MegaMenuHoverEvent()
        );
      } else {
        this.#hoverDispatchTimer =
          setTimeout(() => {
            this.#hoverDispatchTimer =
              undefined;

            if (
              this.#state.activeItem ===
              committedItem
            ) {
              this.dispatchEvent(
                new MegaMenuHoverEvent()
              );
            }
          }, HOVER_COMMIT_DELAY_MS);
      }

      /**
       * Mark submenu active for
       * content visibility optimization.
       */
      submenu.dataset.active =
        '';

      submenu.inert =
        false;

      this.#cleanupMutationObserver();

      this.#submenuMutationObserver =
        new MutationObserver(
          () => {
            requestAnimationFrame(
              () => {
                requestAnimationFrame(
                  () => {
                    if (
                      submenu.offsetHeight >
                      0
                    ) {
                      this.headerComponent
                        ?.style
                        .setProperty(
                          '--submenu-height',
                          `${submenu.offsetHeight}px`
                        );

                      this.#cleanupMutationObserver();
                    }
                  }
                );
              }
            );
          }
        );

      this.#submenuMutationObserver.observe(
        submenu,
        {
          childList: true,
          subtree: true,
        }
      );

      setTimeout(() => {
        this.#cleanupMutationObserver();
      }, 500);
    }

    let finalHeight =
      submenu?.offsetHeight ??
      0;

    /**
     * Overflow-menu height handling.
     */
    if (!isDefaultSlot) {
      const overflowListHeight =
        this.#getOverflowListLinksHeight();

      if (hasSubmenu) {
        const overflowHeight =
          this.overflowMenu
            ?.offsetHeight ??
          0;

        finalHeight =
          Math.max(
            overflowHeight,
            overflowListHeight
          );
      } else {
        finalHeight =
          overflowListHeight;
      }
    }

    if (!submenu) {
      finalHeight = 0;
    }

    const headerVisibleHeight =
      this.#getHeaderVisibleHeight();

    this.headerComponent
      .style
      .setProperty(
        '--submenu-height',
        `${finalHeight}px`
      );

    this.#setFullOpenHeaderHeight(
      finalHeight,
      headerVisibleHeight
    );

    this.style.setProperty(
      '--submenu-opacity',
      '1'
    );

    this.#startPointerTracking(
      item,
      previouslyActiveItem
    );
  }

  /**
   * Menu deactivation handler.
   *
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (
      !(event.target instanceof Element)
    ) {
      return;
    }

    const activeItem =
      this.#state.activeItem;

    const eventItem =
      findMenuItem(
        event.target
      );

    const menu =
      findSubmenu(
        activeItem
      );

    /**
     * Keep the original focus/keyboard logic.
     */
    if (
      event.type !== 'pointerleave'
    ) {
      const isMovingWithinItem =
        event.relatedTarget instanceof Node &&
        event.target instanceof Element &&
        event.target.contains(
          event.relatedTarget
        );

      const isMovingWithinMenu =
        event.relatedTarget instanceof Node &&
        menu?.contains(
          document.activeElement
        );

      const isMovingToSubmenu =
        event.relatedTarget instanceof Node &&
        event.type === 'blur' &&
        menu?.contains(
          event.relatedTarget
        );

      const isMovingToOverflowMenu =
        event.relatedTarget instanceof Element &&
        Boolean(
          event.relatedTarget.closest(
            '[slot="overflow"]'
          )
        );

      if (
        isMovingWithinItem ||
        isMovingWithinMenu ||
        isMovingToOverflowMenu ||
        isMovingToSubmenu
      ) {
        if (activeItem) {
          this.#stopPointerTracking(
            activeItem
          );
        }

        return;
      }

      this.#deactivate();

      return;
    }

    /**
     * If the pointer just left a menu
     * that was waiting to become active,
     * cancel that pending switch.
     */
    if (
      eventItem &&
      eventItem ===
        this.#menuSwitchItem
    ) {
      this.#cancelMenuSwitch();
    }

    const relatedTarget =
      event.relatedTarget;

    /**
     * Critical behavior:
     *
     * If the pointer is still somewhere
     * inside header-menu, do NOT close
     * the currently open Mega Menu.
     *
     * This is what allows:
     *
     * SHOP -> briefly over WHY ->
     * continue diagonally into T SERIES
     *
     * without SHOP disappearing.
     */
    if (
      relatedTarget instanceof Node &&
      this.contains(
        relatedTarget
      )
    ) {
      return;
    }

    /**
     * Shadow-root overflow handling.
     */
    if (
      this.overflowListHovered ||
      this.overflowMenu
        ?.matches(':hover')
    ) {
      return;
    }

    /**
     * The pointer truly left the navigation.
     */
    this.#scheduleMenuClose();
  }

  /**
   * Schedule closing with a short grace period.
   */
  #scheduleMenuClose() {
    clearTimeout(
      this.#menuCloseTimer
    );

    this.#menuCloseTimer =
      setTimeout(() => {
        this.#menuCloseTimer =
          undefined;

        const activeItem =
          this.#state.activeItem;

        if (!activeItem) {
          return;
        }

        const activeListItem =
          activeItem.closest(
            '.menu-list__list-item'
          );

        const activeSubmenu =
          findSubmenu(
            activeItem
          );

        /**
         * Pointer came back before
         * the grace period ended.
         */
        if (
          activeListItem
            ?.matches(':hover') ||
          activeSubmenu
            ?.matches(':hover') ||
          this.overflowListHovered ||
          this.overflowMenu
            ?.matches(':hover')
        ) {
          return;
        }

        this.#deactivate();
      }, MENU_CLOSE_DELAY_MS);
  }

  /**
   * Immediately deactivate current item.
   *
   * @param {HTMLElement | null} item
   * @param {Object} options
   */
  #deactivate = (
    item =
      this.#state.activeItem,
    options = {}
  ) => {
    const force =
      options.force === true;

    if (
      !item ||
      item !==
        this.#state.activeItem
    ) {
      return;
    }

    this.#cancelMenuSwitch();

    clearTimeout(
      this.#menuCloseTimer
    );

    this.#menuCloseTimer =
      undefined;

    if (
      !force &&
      (
        this.overflowListHovered ||
        this.overflowMenu
          ?.matches(':hover')
      )
    ) {
      return;
    }

    clearTimeout(
      this.#hoverDispatchTimer
    );

    this.#hoverDispatchTimer =
      undefined;

    this.headerComponent
      ?.style
      .setProperty(
        '--submenu-height',
        '0px'
      );

    this.#setFullOpenHeaderHeight(
      0,
      0
    );

    this.style.setProperty(
      '--submenu-opacity',
      '0'
    );

    this.dataset.overflowExpanded =
      'false';

    const submenu =
      findSubmenu(item) ||
      (
        item.closest(
          '[slot="more"]'
        )
          ? this.overflowMenu
          : null
      );

    const activeOverflowItem =
      this.#state.activeOverflowItem;

    document.body.removeEventListener(
      'pointermove',
      this.#onPointerMove
    );

    this.#stopPointerTracking(
      item
    );

    this.#state.activeItem =
      null;

    this.#state.activeOverflowItem =
      null;

    this.ariaExpanded =
      'false';

    this.#expandableFor(
      item
    ).ariaExpanded =
      'false';

    if (
      activeOverflowItem &&
      activeOverflowItem !== item
    ) {
      this.#expandableFor(
        activeOverflowItem
      ).ariaExpanded =
        'false';

      const activeOverflowSubmenu =
        findSubmenu(
          activeOverflowItem
        );

      if (
        activeOverflowSubmenu
      ) {
        activeOverflowSubmenu.inert =
          true;
      }
    }

    if (submenu) {
      delete submenu.dataset.active;

      submenu.inert =
        true;
    }
  };

  /**
   * Measure overflow-list height.
   */
  #getOverflowListLinksHeight() {
    const slottedMenuLinks =
      this.overflowMenu
        ?.querySelector('slot')
        ?.assignedElements();

    if (!slottedMenuLinks) {
      return (
        this.overflowMenu
          ?.offsetHeight ??
        0
      );
    }

    /**
     * @param {Function} callback
     */
    const mapSubmenus = (
      callback
    ) => {
      slottedMenuLinks.forEach(
        (link) => {
          const submenu =
            link.querySelector(
              '[ref="submenu[]"]'
            );

          if (
            submenu instanceof HTMLElement
          ) {
            callback(
              submenu
            );
          }
        }
      );
    };

    mapSubmenus(
      (submenu) => {
        submenu.style.setProperty(
          'display',
          'none'
        );
      }
    );

    const height =
      this.overflowMenu
        ?.offsetHeight ??
      0;

    mapSubmenus(
      (submenu) => {
        submenu.style.removeProperty(
          'display'
        );
      }
    );

    return height;
  }

  /**
   * Get visible header height.
   *
   * @returns {number}
   */
  #getHeaderVisibleHeight() {
    if (!this.headerComponent) {
      return 0;
    }

    const isOverlapSituation =
      this.headerComponent
        .hasAttribute(
          'data-submenu-overlap-bottom-row'
        );

    if (
      isOverlapSituation &&
      this.headerComponent
        .offsetHeight >
        0
    ) {
      const topRow =
        this.headerComponent
          .querySelector(
            '.header__row--top'
          );

      if (
        topRow instanceof HTMLElement
      ) {
        return topRow.offsetHeight;
      }

      return 0;
    }

    return (
      this.headerComponent
        .offsetHeight
    );
  }

  /**
   * Set full open header height.
   *
   * @param {number} submenuHeight
   * @param {number} headerVisibleHeight
   */
  #setFullOpenHeaderHeight(
    submenuHeight,
    headerVisibleHeight
  ) {
    if (!this.headerComponent) {
      return;
    }

    const nothingToOpen =
      submenuHeight === 0;

    const fullOpenHeaderHeight =
      nothingToOpen
        ? 0
        : submenuHeight +
          headerVisibleHeight;

    this.headerComponent
      .style
      .setProperty(
        '--full-open-header-height',
        `${fullOpenHeaderHeight}px`
      );
  }

  /**
   * Preload menu images.
   */
  #preloadImages = () => {
    const images =
      this.querySelectorAll(
        'img[loading="lazy"]'
      );

    images.forEach(
      (image) => {
        image.removeAttribute(
          'loading'
        );
      }
    );
  };

  /**
   * Disconnect submenu mutation observer.
   */
  #cleanupMutationObserver() {
    this.#submenuMutationObserver
      ?.disconnect();

    this.#submenuMutationObserver =
      null;
  }
}

if (
  !customElements.get(
    'header-menu'
  )
) {
  customElements.define(
    'header-menu',
    HeaderMenu
  );
}

/**
 * Find the closest menu item.
 *
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (
    !(element instanceof Element)
  ) {
    return null;
  }

  const listItem =
    element.closest(
      '.menu-list__list-item'
    ) ?? element;

  const item =
    listItem.querySelector(
      '[ref="menuitem"]'
    );

  return (
    item instanceof HTMLElement
      ? item
      : null
  );
}

/**
 * Find the submenu belonging
 * to a menu item.
 *
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const listItem =
    element?.closest?.(
      '.menu-list__list-item'
    );

  const submenu =
    listItem?.querySelector(
      ':scope > [ref="submenu[]"]'
    );

  return (
    submenu instanceof HTMLElement
      ? submenu
      : null
  );
}